import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { DockviewApi } from "dockview-react";
import {
  setActiveWorkspace,
  useLinkGroups,
  useSettings,
  type ConnectionView,
  type FormFactor,
  type Me,
} from "@helm/core";

import { matchTradingHotkey } from "@helm/ui";
import { Workspace } from "../workspace/Workspace";
import { WorkspaceBar } from "../workspace/WorkspaceBar";
import { workspaceStore } from "../workspace/workspaceStore";
import { useChartOnly } from "../workspace/chartOnly";
import { CommandPalette } from "../palette/CommandPalette";
import { startAlertEngine } from "../alerts/alertEngine";
import { runChartAlerts } from "../alerts/chartAlertsGate";
import { TopBar } from "../topbar/TopBar";
import { PositionStrip } from "../topbar/PositionStrip";
import { openLockoutPanel } from "../topbar/lockoutEvents";
import { ToastHost, pushToast } from "../components/Toasts";
import { buildRegistry } from "./commands";
import { BracketEditorHost } from "../brackets/BracketEditorDialog";
import { bracketStore } from "../brackets/bracketStore";
import { TradingHosts } from "../trading/hosts";
import { FlattenWaitingBanner } from "../trading/FlattenWaitingBanner";
import { UnconfirmedPositionsBanner } from "../trading/UnconfirmedPositionsBanner";
import { hotkeyTargetOf } from "../trading/hotkeys";
import { requestFlattenAll } from "../trading/actions";
import { TopToolbar } from "../toolbar/TopToolbar";
import { RightSidebar } from "../sidebar/RightSidebar";
import { watchlistStore } from "../sidebar/watchlistStore";
import { SettingsDialogHost } from "../settings/SettingsDialogHost";
import { openSettingsDialog } from "../settings/settingsDialog";
import { CopyTraderHost, RithmicDialogHost } from "../topbar/TopBarDialogHosts";
import { CoachSlot } from "../coach/coachGate";

/** An element from this window or a pop-out window (other realm: no `instanceof`). */
function asElement(t: EventTarget | null): HTMLElement | null {
  const n = t as { nodeType?: number } | null;
  return n !== null && n.nodeType === 1 ? (t as HTMLElement) : null;
}

function isTextInput(target: EventTarget | null): boolean {
  const t = asElement(target);
  if (!t) return false;
  return t.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName);
}

/**
 * Desktop/tablet frame (SPEC §17.2; owner 2026-10-04 UI clean-up): the status line (connection,
 * account picker, trading state, clock, Settings…), the position strip (position, P&L, DD room,
 * working orders, the workspace tabs with Add module / Lock layout, Flatten), the optional
 * toolbar, safety banners and the dockview workspace. No bottom status bar: its diagnostics open
 * from the connection item; no separate workspace row: the dock has that height.
 *
 * Trading hotkeys (B S J A R F C X, SPEC §9.10) act on the focused DOM ladder or order pad (its
 * symbol, account and quantity), never in text inputs, one press = one action; Ctrl+Shift+F
 * requests Exit All using its confirmation preference. They trade only where every order control
 * does: the mock or Rithmic Test, READY (ADR-0021).
 */
export function DesktopShell({
  formFactor,
  me,
  connection,
  layoutKey,
  defaultTemplate,
}: {
  formFactor: FormFactor;
  me: Me;
  connection: ConnectionView;
  /** Legacy single-layout key; workspaces are stored beside it (the dev preview uses its own). */
  layoutKey?: string;
  /** Template a fresh workspaces document starts from (dev preview `ws=`); default the first. */
  defaultTemplate?: string | undefined;
}): React.JSX.Element {
  const dockRef = useRef<DockviewApi | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const setSymbol = useLinkGroups((s) => s.setSymbol);
  const store = useMemo(() => workspaceStore(layoutKey, defaultTemplate), [layoutKey, defaultTemplate]);
  // Window-wide toolbar (Q-G3), Settings → Appearance; default on.
  const toolbarOn = useSettings((s) => s.settings.top_toolbar);
  // Chart only (workspace/chartOnly.ts): the dock hides its other modules; here the right sidebar
  // and the other tabs of a mixed group hide by CSS (shell.css). Status line, strip, banners stay.
  const chartOnly = useChartOnly();

  // Local price alerts are evaluated while the shell is up, not only while their module is open.
  useEffect(() => startAlertEngine(), []);
  // Alerts on drawings / indicators: assigned to this window's charts while the shell is up.
  useEffect(() => runChartAlerts(), []);

  useEffect(() => {
    void store.start();
    // Bracket strategies (bracket_presets/main); the dev preview's store is local-only.
    const brackets = bracketStore();
    void brackets.start();
    // Watchlists (watchlist/main): the right sidebar and the Watchlist module share them.
    const watchlists = watchlistStore();
    const flush = (): void => {
      if (document.visibilityState !== "hidden") return;
      store.flush();
      brackets.flush();
      watchlists.flush();
    };
    document.addEventListener("visibilitychange", flush);
    return () => {
      document.removeEventListener("visibilitychange", flush);
    };
  }, [store]);

  const { registry, tradingCommands } = useMemo(
    () =>
      buildRegistry({
        dock: () => dockRef.current,
        workspaces: store,
        setSymbol,
        openLockout: openLockoutPanel,
      }),
    [setSymbol, store],
  );

  const onKey = useCallback(
    (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      // Ctrl+1…9: switch workspace tab (SPEC §17.3; browsers keep these in a normal tab, the
      // installed app window gets them).
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && /^[1-9]$/.test(e.key)) {
        const w = store.doc.workspaces[Number(e.key) - 1];
        if (w) {
          e.preventDefault();
          store.update((d) => setActiveWorkspace(d, w.id));
        }
        return;
      }
      const t = asElement(e.target);
      const action = matchTradingHotkey(e, t?.closest("[data-trading-scope]") != null, isTextInput(e.target));
      if (!action) return;
      e.preventDefault();
      if (action.id === "trade.flatten_all") {
        requestFlattenAll();
        return;
      }
      const handler = hotkeyTargetOf(t);
      if (!handler || !t) {
        pushToast({
          tone: "info",
          title: `${action.hotkey} · ${action.title}`,
          body: "Trading hotkeys act on a focused DOM ladder or order pad (chart trading is not wired yet)",
        });
        return;
      }
      handler(action.id, t);
    },
    [store],
  );

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [onKey]);

  // Pop-out windows share this JS context: route their keys through the same handler.
  const onPopoutWindow = useCallback(
    (win: Window) => {
      win.addEventListener("keydown", onKey);
      return () => {
        win.removeEventListener("keydown", onKey);
      };
    },
    [onKey],
  );

  const onApi = useCallback((a: DockviewApi | null) => {
    dockRef.current = a;
  }, []);

  return (
    <div
      className={toolbarOn ? "shell shell-desktop with-toolbar" : "shell shell-desktop"}
      data-form-factor={formFactor}
      data-chart-only={chartOnly ? "1" : undefined}
    >
      <TopBar
        me={me}
        connection={connection}
        onOpenPalette={() => {
          setPaletteOpen(true);
        }}
        onOpenSettings={openSettingsDialog}
      />
      <PositionStrip connection={connection} dock={() => dockRef.current}>
        <WorkspaceBar store={store} dock={() => dockRef.current} />
      </PositionStrip>
      {toolbarOn && <TopToolbar store={store} dock={() => dockRef.current} />}
      <div className="shell-banners">
        <UnconfirmedPositionsBanner />
        <FlattenWaitingBanner />
        <CoachSlot />
      </div>
      <main className="workspace ws-with-sidebar">
        <div className="ws-dock">
          <Workspace store={store} onApi={onApi} onPopoutWindow={onPopoutWindow} />
        </div>
        <RightSidebar store={store} onPopoutWindow={onPopoutWindow} />
      </main>
      <TradingHosts />
      <CommandPalette
        registry={registry}
        unavailable={tradingCommands}
        open={paletteOpen}
        onClose={() => {
          setPaletteOpen(false);
        }}
      />
      <BracketEditorHost />
      <SettingsDialogHost />
      <RithmicDialogHost />
      <CopyTraderHost />
      <ToastHost />
    </div>
  );
}
